<template>
	<div class="container mx-auto">
		<div
			v-if="showForm"
			class="grid grid-cols-1 gap-12 pt-8"
		>
			<Checklist
				:image="ANIMATED_TRANSMIT_IMAGE"
				:features="businessFeaturesChecklist"
				:title="$t('business-internet.h2')"
			/>
			<section class="py-8 mx-4 border-t">
				<h2 class="w-full text-4xl font-semibold tracking-tight text-center text-gray-900">
					{{ $t('business-internet.form.title') }}
				</h2>
				<BusinessForm @submit-response="handleSubmitResponse" />
			</section>
		</div>
		<SignupComplete
			v-else
			:thank-you-text="$t('business-internet.thank-you')"
		/>
	</div>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'
import {
	STORYBLOK_VERSION,
	COMPANY_NAME,
	COMPANY_PHONE,
	BASE_URL
} from '@/constants/index.js'
import { SUCCESS } from '@/constants/form/states.js'
import type { ChecklistProps } from '@/components/checklist/types'
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'

import {
	JSON_LD_LOGO,
	JSON_LD_ORGANIZATION_SAME_AS,
	// JSON_LD_ORGANIZATION_REVIEW,
	// JSON_LD_HAS_OFFER_CATALOG,
	JSON_LD_ORGANIZATION_CONTACT_POINT,
	JSON_LD_ORGANIZATION_ADDRESS,
	JSON_LD_ORGANIZATION_AREA_SERVED
} from '@/constants/json-ld'

const { tm, t, locale } = useI18n({
	useScope: 'global'
})

const route = useRoute()

const { getStory } = useStoryblokWithErrorReporting()

useHead({
	title: t('business-internet.meta-title'),
	meta: [
		{ name: 'description', content: t('about.meta-description') },
		{ name: 'url', content: `${BASE_URL}/business-internet` }
	],
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicon.ico'
		}
	]
})

const story = await getStory('business-internet', {
	version: STORYBLOK_VERSION,
	language: locale.value
})

const { title = '', description = '' } = story?.value?.content?.metatags

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogImage: () => '/images/logo.svg',
	ogUrl: () => BASE_URL,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => '/images/logo.svg',
	twitterCard: () => 'summary_large_image'
})

const businessFeaturesChecklist: ChecklistProps[] = tm('business-internet.features')

const ANIMATED_TRANSMIT_IMAGE = {
	id: 'business-internet-image',
	src: '/images/laptop-guy.webp',
	srcset: '/images/laptop-guy-sm.webp 800w, /images/laptop-guy.webp 1200w',
	alt: t('business-internet.image-alt')
}

const showForm = ref(true)
const handleSubmitResponse = (res: string) => {
	if (res === SUCCESS) {
		showForm.value = false
	}
}

useJsonld({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: BASE_URL
				}, {
					'@type': 'ListItem',
					position: 2,
					name: 'Business Internet',
					item: `${BASE_URL}${route.fullPath}`
				}
			]
		},
		{
			'@type': 'Service',
			name: COMPANY_NAME,
			logo: JSON_LD_LOGO,
			provider: {
				'@type': 'Organization',
				name: COMPANY_NAME,
				telephone: COMPANY_PHONE
			},
			serviceType: 'High-speed Business Internet',
			serviceOutput: 'Internet service',
			areaServed: JSON_LD_ORGANIZATION_AREA_SERVED
		},
		{
			'@type': 'Organization',
			name: COMPANY_NAME,
			logo: JSON_LD_LOGO,
			url: BASE_URL,
			telephone: COMPANY_PHONE,
			description: t('meta-description', { COMPANY_NAME }),
			contactPoint: JSON_LD_ORGANIZATION_CONTACT_POINT,
			sameAs: JSON_LD_ORGANIZATION_SAME_AS,
			address: JSON_LD_ORGANIZATION_ADDRESS,
			areaServed: JSON_LD_ORGANIZATION_AREA_SERVED
		}
	]
})
</script>
